import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type TemplateField, type LocalizedText, type ReportSection, resolveLocalized, ReportTemplateDTOSchema, MOH_FORM_IDS, MOH_FORM_LABELS } from "@arna-med/shared";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Pencil,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Spinner } from "@/components/ui/spinner";
import { Loader2 } from "lucide-react";
import { useSpecialities, useCreateSpeciality, useUpdateSpeciality, useDeleteSpeciality } from "@/hooks/api/useSpecialities";
import { useTemplates, useSaveTemplate, useToggleTemplate, useDeleteTemplate } from "@/hooks/api/useTemplates";
import { useReportStructures } from "@/hooks/api/useReportStructures";

type ReportTemplateDTO = z.infer<typeof ReportTemplateDTOSchema>;
type FieldImportance = "blocking" | "normal" | "extra";
type FieldType = "text" | "select" | "measurements";

const EMPTY_FIELD = (): TemplateField => ({
  key: "",
  label: "",
  bilateral: false,
  importance: "normal",
  type: "text",
});

// Languages offered when authoring localizable labels/options.
const FIELD_LANGS: { code: string; label: string }[] = [
  { code: "ru", label: "RU" },
  { code: "kz", label: "KZ" },
  { code: "en", label: "EN" },
];

// Exact value stored for a single language. A legacy plain string is treated as
// Russian (that is how existing templates were authored).
function langValue(v: LocalizedText | undefined | null, lang: string): string {
  if (v == null) return "";
  if (typeof v === "string") return lang === "ru" ? v : "";
  const val = v[lang];
  return typeof val === "string" ? val : "";
}

// ─── Field Editor (shared between create and edit) ─────────────────
function FieldEditor({
  fields,
  sections,
  onChange,
}: {
  fields: TemplateField[];
  // Sections of the report structure this template is linked to — empty when
  // the structure could not be resolved, in which case the per-field section
  // picker is hidden rather than shown with no options.
  sections: ReportSection[];
  onChange: (fields: TemplateField[]) => void;
}) {
  const { t, i18n } = useTranslation();

  const [confirmFieldIdx, setConfirmFieldIdx] = useState<number | null>(null);
  // Raw comma text keyed by `${fieldIdx}:${lang}` while the user is typing options.
  const [rawOptionsMap, setRawOptionsMap] = useState<Record<string, string>>({});

  const update = (index: number, patch: Partial<TemplateField>) =>
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  const add = () => onChange([...fields, EMPTY_FIELD()]);
  const remove = (index: number) =>
    onChange(fields.filter((_, i) => i !== index));

  // Set one language of a field's label, preserving the others.
  const setLabelLang = (index: number, lang: string, text: string) => {
    const cur = fields[index].label;
    const base: Record<string, string> =
      typeof cur === "string"
        ? cur
          ? { ru: cur }
          : {}
        : { ...(cur as Record<string, string>) };
    if (text) base[lang] = text;
    else delete base[lang];
    update(index, { label: base });
  };

  // Current comma text for a field's options in one language.
  const optionsRaw = (index: number, lang: string): string => {
    const key = `${index}:${lang}`;
    if (key in rawOptionsMap) return rawOptionsMap[key];
    const values = (fields[index].options ?? []).map((o) =>
      langValue(o as LocalizedText, lang),
    );
    // Show nothing (placeholder) for a language with no translations yet,
    // instead of a string of empty positional commas.
    if (values.every((v) => v === "")) return "";
    return values.join(", ");
  };

  // Combine the three per-language comma lists into positionally-aligned
  // localized option objects, then clear the raw editing state for this field.
  const commitOptions = (index: number) => {
    const split = (lang: string) =>
      optionsRaw(index, lang)
        .split(",")
        .map((s) => s.trim());
    const ru = split("ru");
    const kz = split("kz");
    const en = split("en");
    const count = Math.max(ru.length, kz.length, en.length);
    const options: LocalizedText[] = [];
    for (let i = 0; i < count; i++) {
      const o: Record<string, string> = {};
      if (ru[i]) o.ru = ru[i];
      if (kz[i]) o.kz = kz[i];
      if (en[i]) o.en = en[i];
      if (Object.keys(o).length > 0) options.push(o);
    }
    update(index, { options });
    setRawOptionsMap((prev) => {
      const next = { ...prev };
      for (const { code } of FIELD_LANGS) delete next[`${index}:${code}`];
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <Label className="text-xs">{t("templates.fields")}</Label>
      {fields.map((field, idx) => (
        <div key={idx} className="rounded-md border p-3 space-y-2 bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              {t("templates.field")} {idx + 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setConfirmFieldIdx(idx)}
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("templates.fieldKey")}</Label>
            <Input
              value={field.key}
              onChange={(e) => update(idx, { key: e.target.value })}
              placeholder="e.g. kidneyLength"
              className="text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("templates.fieldLabel")}</Label>
            <div className="grid grid-cols-3 gap-2">
              {FIELD_LANGS.map(({ code, label }) => (
                <div key={code} className="space-y-1">
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {label}
                  </span>
                  <Input
                    value={langValue(field.label, code)}
                    onChange={(e) => setLabelLang(idx, code, e.target.value)}
                    placeholder={code === "ru" ? "Длина почки" : code === "kz" ? "Бүйрек ұзындығы" : "Kidney length"}
                    className="text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("templates.fieldType")}</Label>
              <select
                value={field.type}
                onChange={(e) =>
                  update(idx, { type: e.target.value as FieldType })
                }
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="text">text</option>
                <option value="select">select</option>
                <option value="measurements">measurements</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("templates.importance")}</Label>
              <select
                value={field.importance}
                onChange={(e) =>
                  update(idx, { importance: e.target.value as FieldImportance })
                }
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="blocking">blocking</option>
                <option value="normal">normal</option>
                <option value="extra">extra</option>
              </select>
            </div>
          </div>
          {sections.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs">{t("templates.fieldSection")}</Label>
              <select
                value={field.sectionKey ?? ""}
                onChange={(e) =>
                  update(idx, { sectionKey: e.target.value || null })
                }
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="">{t("templates.fieldSectionAuto")}</option>
                {sections.map((s) => (
                  <option key={s.key} value={s.key}>
                    {resolveLocalized(s.title, i18n.language)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                {t("templates.fieldSectionHint")}
              </p>
            </div>
          )}
          {field.type === "select" && (
            <div className="space-y-1">
              <Label className="text-xs">{t("templates.options")}</Label>
              {FIELD_LANGS.map(({ code, label }) => (
                <div key={code} className="flex items-center gap-2">
                  <span className="w-6 text-[10px] font-medium text-muted-foreground">
                    {label}
                  </span>
                  <Input
                    value={optionsRaw(idx, code)}
                    onChange={(e) =>
                      setRawOptionsMap((prev) => ({
                        ...prev,
                        [`${idx}:${code}`]: e.target.value,
                      }))
                    }
                    onBlur={() => commitOptions(idx)}
                    placeholder="Option A, Option B, Option C"
                    className="text-xs"
                  />
                </div>
              ))}
              <p className="text-xs text-muted-foreground">
                {t("templates.optionsHint")}
              </p>
            </div>
          )}
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={field.bilateral}
              onChange={(e) => update(idx, { bilateral: e.target.checked })}
            />
            {t("templates.bilateral")}
          </label>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="w-full">
        <Plus className="h-3 w-3 mr-1" />
        {t("templates.addField")}
      </Button>
    <ConfirmDialog
      open={confirmFieldIdx !== null}
      onOpenChange={(open) => { if (!open) setConfirmFieldIdx(null); }}
      onConfirm={() => { remove(confirmFieldIdx!); setConfirmFieldIdx(null); }}
    />
  </div>
  );
}

// ─── Inline template edit form ─────────────────────────────────────
type DraftForm = { name: string; nameRu: string; nameKz: string; llmPrompt: string; fields: TemplateField[]; mohFormId?: string | null; isGeneral: boolean; reportStructureId?: string | null };

function TemplateEditForm({
  template,
  specialityId,
  draft,
  onDraftChange,
  onClose,
}: {
  template: ReportTemplateDTO | null; // null = create new
  specialityId: string;
  draft?: DraftForm;
  onDraftChange: (draft: DraftForm) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: structures = [] } = useReportStructures();

  const [formData, setFormData] = useState<DraftForm>({
    name: draft?.name ?? template?.name ?? "",
    nameRu: draft?.nameRu ?? template?.nameRu ?? "",
    nameKz: draft?.nameKz ?? template?.nameKz ?? "",
    llmPrompt: draft?.llmPrompt ?? template?.llmPrompt ?? "",
    fields: draft?.fields ?? (template ? (template.fields as TemplateField[]) : [EMPTY_FIELD()]),
    mohFormId: draft?.mohFormId !== undefined ? draft.mohFormId : (template?.mohFormId ?? null),
    isGeneral: draft?.isGeneral ?? template?.isGeneral ?? false,
    reportStructureId:
      draft?.reportStructureId !== undefined
        ? draft.reportStructureId
        : (template?.reportStructureId ?? null),
  });
  const [warnings, setWarnings] = useState<{ fieldKey: string; fieldLabel: string; message: string }[]>([]);

  const update = (patch: Partial<DraftForm>) => {
    const next = { ...formData, ...patch };
    setFormData(next);
    onDraftChange(next);
  };

  const saveMutation = useSaveTemplate(template, specialityId);

  const defaultStructure = structures.find((s) => s.isDefault);
  // Fields are mapped against whichever structure this template will use, so an
  // unlinked template maps against the default one rather than showing nothing.
  const activeStructure = formData.reportStructureId
    ? structures.find((s) => s.id === formData.reportStructureId)
    : defaultStructure;

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (formData.fields.length === 0) {
      toast.error("At least one field is required");
      return;
    }
    // A field is valid if it has a key and a label in at least one language.
    const invalidField = formData.fields.find(
      (f) => !f.key.trim() || !resolveLocalized(f.label, "ru"),
    );
    if (invalidField) {
      toast.error("All fields must have a key and label");
      return;
    }
    saveMutation.mutate(
      {
        name: formData.name.trim(),
        nameRu: formData.nameRu.trim() || undefined,
        nameKz: formData.nameKz.trim() || undefined,
        fields: formData.fields,
        llmPrompt: formData.llmPrompt.trim() || undefined,
        mohFormId: formData.mohFormId ?? null,
        isGeneral: formData.isGeneral,
        reportStructureId: formData.reportStructureId ?? null,
      },
      {
        onSuccess: (response: unknown) => {
          const w = (response as { warnings?: { fieldKey: string; fieldLabel: string; message: string }[] })?.warnings ?? [];
          if (w.length > 0) {
            setWarnings(w);
          } else {
            onClose();
          }
        },
      },
    );
  };

  return (
    <div className="border-t px-4 pb-4 pt-3 space-y-4 bg-muted/20">
      <div className="space-y-1">
        <Label className="text-xs">{t("templates.templateName")} (EN)</Label>
        <Input
          value={formData.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder={t("templates.templateNamePlaceholder")}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("templates.templateName")} (RU)</Label>
        <Input
          value={formData.nameRu}
          onChange={(e) => update({ nameRu: e.target.value })}
          placeholder={t("templates.templateNamePlaceholderRu")}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("templates.templateName")} (KZ)</Label>
        <Input
          value={formData.nameKz}
          onChange={(e) => update({ nameKz: e.target.value })}
          placeholder={t("templates.templateNamePlaceholderKz")}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("templates.mohForm")}</Label>
        <select
          value={formData.mohFormId ?? ""}
          onChange={(e) => update({ mohFormId: e.target.value || null })}
          className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="">{t("templates.mohFormNone")}</option>
          {MOH_FORM_IDS.map((id) => (
            <option key={id} value={id}>
              {MOH_FORM_LABELS[id]}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">{t("templates.mohFormHint")}</p>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.isGeneral}
          onChange={(e) => update({ isGeneral: e.target.checked })}
        />
        <span className="text-sm font-medium">{t("templates.isGeneral")}</span>
        <span className="text-xs text-muted-foreground">{t("templates.isGeneralHint")}</span>
      </label>
      <div className="space-y-1">
        <Label className="text-xs">{t("templates.reportStructure")}</Label>
        <select
          value={formData.reportStructureId ?? ""}
          onChange={(e) => update({ reportStructureId: e.target.value || null })}
          className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="">
            {defaultStructure
              ? `${t("templates.reportStructureDefault")} — ${resolveLocalized(
                  { ru: defaultStructure.nameRu ?? defaultStructure.name, kz: defaultStructure.nameKz ?? defaultStructure.name, en: defaultStructure.name },
                  i18n.language,
                )}`
              : t("templates.reportStructureDefault")}
          </option>
          {structures.map((s) => (
            <option key={s.id} value={s.id}>
              {resolveLocalized(
                { ru: s.nameRu ?? s.name, kz: s.nameKz ?? s.name, en: s.name },
                i18n.language,
              )}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">{t("templates.reportStructureHint")}</p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("templates.llmPrompt")}</Label>
        <textarea
          className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none outline-none"
          value={formData.llmPrompt}
          onChange={(e) => update({ llmPrompt: e.target.value })}
          placeholder={t("templates.llmPromptPlaceholder")}
        />
      </div>
      <FieldEditor
        fields={formData.fields}
        sections={activeStructure?.sections ?? []}
        onChange={(fields) => update({ fields })}
      />
      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs space-y-1">
          <p className="font-semibold text-amber-800">⚠️ {t("templates.mohWarningTitle", { count: warnings.length })}</p>
          <ul className="list-disc pl-4 text-amber-700 space-y-0.5">
            {warnings.map((w) => (
              <li key={w.fieldKey}>{w.fieldLabel}</li>
            ))}
          </ul>
          <p className="text-amber-600 mt-1">{t("templates.mohWarningHint")}</p>
        </div>
      )}
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        {warnings.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => { setWarnings([]); onClose(); }}>
            {t("templates.saveAnyway")}
          </Button>
        )}
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {warnings.length > 0 ? t("templates.fixAndRetry") : t("common.save")}
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────
export default function TemplateManager() {
  const { t } = useTranslation();

  const [expandedSpeciality, setExpandedSpeciality] = useState<string | null>(
    null,
  );
  const [showSpecialityForm, setShowSpecialityForm] = useState(false);
  const [newSpeciality, setNewSpeciality] = useState("");
  const [newSpecialityRu, setNewSpecialityRu] = useState("");
  const [newSpecialityKz, setNewSpecialityKz] = useState("");
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [draftForms, setDraftForms] = useState<Record<string, DraftForm>>({});
  const [confirmSpecialityId, setConfirmSpecialityId] = useState<string | null>(null);
  const [confirmTemplateId, setConfirmTemplateId] = useState<string | null>(null);
  const [editingSpecialityId, setEditingSpecialityId] = useState<string | null>(null);
  const [editingSpecialityName, setEditingSpecialityName] = useState("");
  const [editingSpecialityNameRu, setEditingSpecialityNameRu] = useState("");
  const [editingSpecialityNameKz, setEditingSpecialityNameKz] = useState("");

  const handleDraftChange = (key: string, draft: DraftForm) => {
    setDraftForms((prev) => ({ ...prev, [key]: draft }));
  };

  const handleClearDraft = (key: string) => {
    setDraftForms((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const { data: specialities = [] } = useSpecialities();
  const { data: templates = [], isLoading: templatesLoading } = useTemplates(expandedSpeciality);
  const createSpecialityMutation = useCreateSpeciality();
  const updateSpecialityMutation = useUpdateSpeciality();
  const deleteSpecialityMutation = useDeleteSpeciality();
  const toggleTemplateMutation = useToggleTemplate(expandedSpeciality);
  const deleteTemplateMutation = useDeleteTemplate(expandedSpeciality);

  const handleToggleSpeciality = (id: string) => {
    setExpandedSpeciality((prev) => (prev === id ? null : id));
    setEditingId(null);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{t("templates.title")}</h1>
          <Button onClick={() => setShowSpecialityForm((v) => !v)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("templates.addSpeciality")}
          </Button>
        </div>

        {/* Add speciality form */}
        {showSpecialityForm && (
          <Card className="mb-4">
            <CardContent className="pt-4 space-y-3">
              <p className="font-medium text-sm">
                {t("templates.addSpeciality")}
              </p>
              <div className="space-y-2">
                <Label>{t("templates.specialityName")} (EN)</Label>
                <Input
                  value={newSpeciality}
                  onChange={(e) => setNewSpeciality(e.target.value)}
                  placeholder="e.g. Cardiology"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("templates.specialityName")} (RU)</Label>
                <Input
                  value={newSpecialityRu}
                  onChange={(e) => setNewSpecialityRu(e.target.value)}
                  placeholder={t("templates.specialityNamePlaceholderRu")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("templates.specialityName")} (KZ)</Label>
                <Input
                  value={newSpecialityKz}
                  onChange={(e) => setNewSpecialityKz(e.target.value)}
                  placeholder={t("templates.specialityNamePlaceholderKz")}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSpecialityForm(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    createSpecialityMutation.mutate(
                      {
                        name: newSpeciality,
                        nameRu: newSpecialityRu || undefined,
                        nameKz: newSpecialityKz || undefined,
                      },
                      {
                        onSuccess: () => {
                          setShowSpecialityForm(false);
                          setNewSpeciality("");
                          setNewSpecialityRu("");
                          setNewSpecialityKz("");
                        },
                      },
                    )
                  }
                  disabled={
                    !newSpeciality || createSpecialityMutation.isPending
                  }
                >
                  {t("common.save")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Specialities list */}
        <div className="space-y-3">
          {specialities.map((sp) => (
            <Card key={sp.id}>
              <CardContent className="p-0">
                {/* Speciality header */}
                {editingSpecialityId === sp.id ? (
                  <div className="flex flex-col gap-2 p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-8">EN</span>
                      <Input
                        className="h-8 font-semibold"
                        value={editingSpecialityName}
                        onChange={(e) => setEditingSpecialityName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-8">RU</span>
                      <Input
                        className="h-8"
                        value={editingSpecialityNameRu}
                        onChange={(e) => setEditingSpecialityNameRu(e.target.value)}
                        placeholder={t("templates.specialityNamePlaceholderRu")}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-8">KZ</span>
                      <Input
                        className="h-8"
                        value={editingSpecialityNameKz}
                        onChange={(e) => setEditingSpecialityNameKz(e.target.value)}
                        placeholder={t("templates.specialityNamePlaceholderKz")}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!editingSpecialityName.trim() || updateSpecialityMutation.isPending}
                        onClick={() => {
                          if (!editingSpecialityName.trim()) return;
                          updateSpecialityMutation.mutate(
                            {
                              id: sp.id,
                              data: {
                                name: editingSpecialityName.trim(),
                                nameRu: editingSpecialityNameRu.trim() || undefined,
                                nameKz: editingSpecialityNameKz.trim() || undefined,
                              },
                            },
                            { onSuccess: () => setEditingSpecialityId(null) },
                          );
                        }}
                      >
                        {updateSpecialityMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingSpecialityId(null)}>
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/50 select-none"
                  onClick={() => handleToggleSpeciality(sp.id)}
                >
                  <div className="flex items-center gap-2">
                    {expandedSpeciality === sp.id ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold">{sp.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSpecialityId(sp.id);
                        setEditingSpecialityName(sp.name);
                        setEditingSpecialityNameRu(sp.nameRu ?? "");
                        setEditingSpecialityNameKz(sp.nameKz ?? "");
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmSpecialityId(sp.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                )}

                {/* Templates list */}
                {expandedSpeciality === sp.id && (
                  <div className="border-t">
                    {templatesLoading ? (
                      <Spinner className="py-6" />
                    ) : (<>
                    {templates.map((tpl) => (
                      <div key={tpl.id}>
                        {/* Template row */}
                        <div className="flex items-center justify-between px-4 py-3 border-b last:border-b-0">
                          <div>
                            <p className="font-medium text-sm">{tpl.name}</p>
                            <p className="text-xs text-muted-foreground">
                              v{tpl.version} ·{" "}
                              {(tpl.fields as TemplateField[]).length}{" "}
                              {t("templates.fields")}
                              {!tpl.isActive && (
                                <span className="ml-2 text-amber-600">
                                  {t("templates.inactive")}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t("templates.editTemplate")}
                              onClick={() =>
                                setEditingId((prev) =>
                                  prev === tpl.id ? null : tpl.id,
                                )
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={
                                tpl.isActive
                                  ? t("templates.deactivate")
                                  : t("templates.activate")
                              }
                              onClick={() =>
                                toggleTemplateMutation.mutate({
                                  id: tpl.id,
                                  isActive: !tpl.isActive,
                                })
                              }
                            >
                              {tpl.isActive ? (
                                <ToggleRight className="h-4 w-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setConfirmTemplateId(tpl.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>

                        {/* Inline edit form for this template */}
                        {editingId === tpl.id && (
                          <TemplateEditForm
                            template={tpl}
                            specialityId={sp.id}
                            draft={draftForms[tpl.id]}
                            onDraftChange={(d) => handleDraftChange(tpl.id, d)}
                            onClose={() => { setEditingId(null); handleClearDraft(tpl.id); }}
                          />
                        )}
                      </div>
                    ))}

                    {/* New template form or button */}
                    {editingId === "new" ? (
                      <TemplateEditForm
                        template={null}
                        specialityId={sp.id}
                        draft={draftForms[`${sp.id}:new`]}
                        onDraftChange={(d) => handleDraftChange(`${sp.id}:new`, d)}
                        onClose={() => { setEditingId(null); handleClearDraft(`${sp.id}:new`); }}
                      />
                    ) : (
                      <div className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => setEditingId("new")}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {t("templates.addTemplate")}
                        </Button>
                      </div>
                    )}
                    </>)}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {specialities.length === 0 && (
            <p className="text-center text-muted-foreground py-12">
              {t("templates.noSpecialities")}
            </p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmSpecialityId !== null}
        onOpenChange={(open) => { if (!open) setConfirmSpecialityId(null); }}
        onConfirm={() => { deleteSpecialityMutation.mutate(confirmSpecialityId!); setConfirmSpecialityId(null); }}
        isPending={deleteSpecialityMutation.isPending}
      />
      <ConfirmDialog
        open={confirmTemplateId !== null}
        onOpenChange={(open) => { if (!open) setConfirmTemplateId(null); }}
        onConfirm={() => { deleteTemplateMutation.mutate(confirmTemplateId!); setConfirmTemplateId(null); }}
        isPending={deleteTemplateMutation.isPending}
      />
    </Layout>
  );
}
